import { MailIcon } from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';

import styles from '../../agents.module.css';

import clsxm from '@/lib/clsxm';

import { CheckGlyph, delay, tone } from '../../primitives';

/**
 * The email a support team gets when the agent hands a case over: the
 * details it collected in the chat and a summary it wrote.
 */
export default function EscalationEmail({ brand }: { brand: string }) {
  const { t } = useLocale('bot-use-cases');
  const { t: tc } = useLocale('chatbots');

  const collected = [tc('Name'), tc('Email'), t('Order number')];
  const facts = [
    { label: t('Customer'), value: 'Maya Chen · maya@example.com' },
    { label: t('Order'), value: `#4821 · ${t('Arrives Thursday')}` },
    { label: t('Request'), value: t('Deliver to a new address') },
  ];

  return (
    <div className='relative pt-0 sm:pt-10'>
      <div
        className={clsxm(
          styles.rv,
          'mb-4 rounded-2xl bg-white/[0.06] p-4 ring-1 ring-inset ring-white/10 sm:absolute sm:end-4 sm:top-0 sm:z-10 sm:mb-0 sm:w-56 sm:bg-[#1b1a33] sm:shadow-[0_24px_50px_-20px_rgba(0,0,0,0.6)]',
        )}
        style={delay(300)}
      >
        <p className='text-xs font-medium text-white/60'>
          {t('Collected in the chat')}
        </p>
        <ul className='mt-2.5 space-y-1.5'>
          {collected.map((field) => (
            <li
              key={field}
              className='flex items-center justify-between gap-3 text-[13px] text-white'
            >
              {field}
              <CheckGlyph className='h-3.5 w-3.5 text-emerald-400' />
            </li>
          ))}
        </ul>
      </div>

      <div
        className={clsxm(
          'overflow-hidden rounded-[22px] shadow-[0_40px_90px_-30px_rgba(0,0,0,0.7)]',
          tone.surface,
          tone.line,
        )}
      >
        <div className='flex items-center gap-3 border-b border-black/[0.06] px-5 py-4 dark:border-white/[0.06]'>
          <span
            className={clsxm(
              'flex h-9 w-9 flex-none items-center justify-center rounded-full text-white',
              brand,
            )}
          >
            <MailIcon className='h-4 w-4' />
          </span>
          <div className='min-w-0 flex-1'>
            <p
              className={clsxm('truncate text-[15px] font-semibold', tone.ink)}
            >
              {t('New request from your agent')}
            </p>
            <p className={clsxm('truncate text-xs', tone.quiet)} dir='ltr'>
              support@example.com
            </p>
          </div>
          <span className={clsxm('text-xs tabular-nums', tone.quiet)}>
            {tc('Just now')}
          </span>
        </div>

        <div className='space-y-4 px-5 py-5 text-[14px] leading-relaxed'>
          <p className={clsxm('font-semibold', tone.ink)}>
            {t('Delivery address change for order #4821')}
          </p>
          <p className={tone.body}>
            {t(
              'Hi team, Maya would like order #4821 sent to a new address. The parcel is already with the courier, so the change has to go through the carrier.',
            )}
          </p>
          <dl className='divide-y divide-black/[0.06] rounded-2xl bg-[#f7f7fa] px-4 text-[13px] dark:divide-white/[0.06] dark:bg-white/[0.04]'>
            {facts.map(({ label, value }) => (
              <div key={label} className='flex gap-4 py-2.5'>
                <dt className={clsxm('w-24 flex-none', tone.quiet)}>{label}</dt>
                <dd className={clsxm('min-w-0 font-medium', tone.ink)}>
                  {value}
                </dd>
              </div>
            ))}
          </dl>
          <p className={clsxm('flex items-center gap-2 text-xs', tone.quiet)}>
            <span className='h-1.5 w-1.5 rounded-full bg-emerald-500' />
            {t('Written by your agent from the chat at 9:42 p.m.')}
          </p>
        </div>
      </div>
    </div>
  );
}
